/**
 * The session panel over a Buddy draft (board R): the builder's own day programming dock, mounted
 * on this page.
 *
 * ── Nothing here is a second panel ───────────────────────────────────────────────────────────
 * Owner, 2 October 2026: the panel drawn on boards Q and R stands in for the one the app already
 * has. So this file is the page's composition and nothing else, the same three pieces
 * `pages/plan-builder`'s `DayDock` composes: `plan-day-programming`'s form in its panel host,
 * `plan-progression`'s rule and matrix slots, and the exercise library rail in the host's drawer.
 * The head with its previous, next and close, the set table, the progression line and the catalogue
 * are all the builder's.
 *
 * What differs from the builder is what this page is: there is one box (no maximise), the sessions
 * are named by the card's letters rather than by a canvas's coordinates, and the foot carries the
 * page's own act, „Otwórz w builderze", beside the sentence that says where the edits live and how
 * their last save went.
 *
 * ── Not modal, like the builder's panel ──────────────────────────────────────────────────────
 * The conversation beside it stays readable and usable: the trainer can decide an exercise outside
 * the catalogue, or write to Buddy, with the session open. A press outside does not dismiss; Escape
 * and the ✕ do, and Escape puts the catalogue away first while it lies over the session.
 */

import { useId } from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

import { useDayPhaseDays } from '~/entities/plan-draft';
import { ExerciseRail } from '~/features/exercise-library-rail';
import { DayForm, DayPanelDialog, type DayFormSave } from '~/features/plan-day-programming';
import { dayProgression } from '~/features/plan-progression';
import { ConfirmDialog } from '~/shared/ui/ConfirmDialog';
import { LiveRegionAnnouncer } from '~/shared/ui/LiveRegionAnnouncer';

import type { BuddySessionDockState, DraftSaveView } from '../lib/useBuddySessionDock';
import { sessionMoveTargets } from '../model/draftSessions';
import type { PlacedViolation } from '../model/draftViolations';

/** One refused rule, with the session and exercise it stands at when its path names them. */
function violationWords(t: TFunction, violation: PlacedViolation): string {
  const rule = t(violation.key);
  if (violation.session === null) return rule;
  const place =
    violation.exercise === null
      ? t('web:aiPlan.session.placeSession', { letter: violation.session })
      : t('web:aiPlan.session.placeExercise', { letter: violation.session, exercise: violation.exercise });
  return t('web:aiPlan.session.violationAt', {
    place: violation.phase === null ? place : t('web:aiPlan.session.placeInPhase', { phase: violation.phase, place }),
    message: rule,
  });
}

/**
 * The foot's sentence: where the edits live, and how the last save of them went. A routine state
 * is quiet and stands where it is; a save that did not go through is an alert.
 */
function saveNote(t: TFunction, save: DraftSaveView): NonNullable<DayFormSave['note']> {
  const quiet = (text: string) => ({ text, tone: 'quiet' as const });
  const alert = (text: string) => ({ text, tone: 'alert' as const });
  switch (save.kind) {
    case 'idle':
      return quiet(t('web:aiPlan.session.editsStay'));
    case 'saving':
      return quiet(t('web:aiPlan.session.saving'));
    case 'saved':
      return quiet(t('web:aiPlan.session.saved'));
    case 'stale':
      return quiet(t('web:aiPlan.session.stale'));
    case 'busy':
      return alert(t('web:aiPlan.session.busy'));
    case 'failed':
      return alert(save.retrying ? t('web:aiPlan.session.failed') : t('web:aiPlan.session.failedStopped'));
    case 'rejected':
      return alert(t('web:aiPlan.session.rejected'));
    case 'gone':
      return alert(t('web:aiPlan.session.gone'));
    case 'invalid':
      // Every problem at once, each where it stands (R56), so the trainer fixes them in one pass.
      return alert(
        save.violations.length === 0
          ? t('web:aiPlan.session.invalid')
          : t('web:aiPlan.session.invalidAll', { message: save.violations.map((violation) => violationWords(t, violation)).join(' ') }),
      );
  }
}

interface BuddySessionDockProps {
  readonly dock: BuddySessionDockState;
  /** Open the draft this session belongs to in the builder, as it was edited here. */
  readonly onOpenInBuilder: () => void;
}

export function BuddySessionDock({ dock: session, onOpenInBuilder }: BuddySessionDockProps) {
  const headingId = useId();
  const { t, i18n } = useTranslation();
  const { dock } = session;
  const progression = dayProgression({ t, locale: i18n.language, announce: dock.announce });
  // The one thing the form takes from its host as a list: the sessions it can step and move to,
  // the days of the open session's own phase. Read narrowly, so an edit in another phase does not
  // re-render the panel; an edit in this phase does, since a target's name and block count are its.
  const phaseDays = useDayPhaseDays(dock.daySlotToken);
  const prompt = session.leavePrompt;

  return (
    <>
      <DayPanelDialog
        modal={false}
        host="panel"
        dock={dock}
        headingId={headingId}
        announcement={dock.announcement}
        saveAnnouncement=""
        form={
          dock.daySlotToken === null ? null : (
            <DayForm
              daySlotToken={dock.daySlotToken}
              headingId={headingId}
              onSelectDay={dock.selectDay}
              onClose={dock.close}
              onRequestLibrary={dock.requestLibrary}
              announce={dock.announce}
              /* The foot's one act on this page is the draft's way to the builder, and the
               * sentence beside it says where the edits made here live (board R). */
              save={{
                label: t('web:aiPlan.program.open'),
                disabledReason:
                  session.undecided > 0 ? t('web:aiPlan.program.decideFirst', { count: session.undecided }) : null,
                onSave: onOpenInBuilder,
                isDirty: false,
                unsavedLabel: '',
                note: saveNote(t, session.save),
              }}
              dayTargets={
                phaseDays === null
                  ? []
                  : sessionMoveTargets(phaseDays, {
                      session: (letter) => t('web:aiPlan.session.target', { letter }),
                      label: (short, title) => t('web:aiPlan.session.targetLabel', { short, title }),
                      untitled: t('web:planBuilder.untitledSession'),
                    })
              }
              progression={progression}
              focusExerciseSlotToken={dock.focusExerciseToken}
            />
          )
        }
        renderLibrary={(props) => <ExerciseRail {...props} />}
      />
      {/* A routine save is said once, when it lands; the foot's sentence is read where it stands. */}
      <LiveRegionAnnouncer message={session.save.kind === 'saved' ? t('web:aiPlan.session.savedShort') : ''} />
      <ConfirmDialog
        prompt={
          prompt === null
            ? null
            : {
                title: t('web:aiPlan.session.leave.title'),
                description: t('web:aiPlan.session.leave.description'),
                confirmLabel: t('web:aiPlan.session.leave.confirm'),
                cancelLabel: t('web:aiPlan.session.leave.cancel'),
                destructiveConfirm: true,
              }
        }
        onConfirm={() => {
          prompt?.proceed();
        }}
        onCancel={() => {
          prompt?.stay();
        }}
      />
    </>
  );
}
